import {
  atypDisaply,
  circularStd,
  galmuri11,
  inter,
  jetbrains,
  microgramma,
  poppins,
  pretendard,
  roboto,
  urbanist,
} from '@/assets/fonts';

import type { TypographyConfig } from './theme';

export type FontConfig = {
  family: string;
  url?: string;
};

export type FontResult = {
  className: string;
  style: {
    fontFamily: string;
  };
  isCustom?: boolean;
};

const FONT_MAP = {
  inter,
  roboto,
  jetbrains,
  poppins,
  urbanist,
  michroma: microgramma,
  atypdisplay: atypDisaply,
  circularstd: circularStd,
  pretendard,
  galmuri11,
} as const;

export const loadFont = async ({ family, url }: FontConfig): Promise<FontResult> => {
  if (url) {
    return {
      className: 'custom-font',
      style: {
        fontFamily: family,
      },
      isCustom: true,
    };
  }

  const normalizedFamily = family.toLowerCase();
  return FONT_MAP[normalizedFamily as keyof typeof FONT_MAP] || FONT_MAP.inter;
};

export const loadTypography = async (typography: TypographyConfig) => {
  const fonts = await Promise.all([
    loadFont(typography.heading),
    loadFont(typography.paragraph),
    loadFont(typography.subText),
  ]);

  // If all fonts are the same, return a single className
  const areFontsSame = fonts.every(font => font.style.fontFamily === fonts[0].style.fontFamily);

  if (areFontsSame) {
    return {
      className: fonts[0].className,
      styles: {
        fontFamily: fonts[0].style.fontFamily,
      },
      customFonts: fonts[0].isCustom ? [typography.heading] : [],
    };
  }

  // If fonts are different, return class names for each type
  return {
    className: 'multiple-fonts',
    styles: {
      heading: {
        fontFamily: fonts[0].style.fontFamily,
        className: fonts[0].className,
      },
      paragraph: {
        fontFamily: fonts[1].style.fontFamily,
        className: fonts[1].className,
      },
      subText: {
        fontFamily: fonts[2].style.fontFamily,
        className: fonts[2].className,
      },
    },
    customFonts: [
      ...(fonts[0].isCustom ? [typography.heading] : []),
      ...(fonts[1].isCustom ? [typography.paragraph] : []),
      ...(fonts[2].isCustom ? [typography.subText] : []),
    ],
  };
};
